import { EmployeeFormProps, EmployeeProps, Roles } from '../../../interfaces/employee_type';
import { useNavigate } from "react-router-dom"
import { EmployeeFormBase } from './EmployeeFormBase';
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { createEmployee, updateEmployee } from '@/api/employee';
import {Error_Component, MethodHandler} from "@/components/misc.tsx";

const employeeSchema = z.object({
    name: z.string().min(1, {message: "Name is required"}),
    email: z.string().email({message: "Invalid email address"}),
    role: z.string().min(1, {message: "Role is required"}),
})

export type EmployeeFormData = z.infer<typeof employeeSchema>

export function EmployeeForm({employee, method}: {employee?: EmployeeProps, method: "POST" | "PUT" }) {
    const employeeData: EmployeeProps = employee ?? {
        name: '',
        email: '',
        role: 'Team Member',
        username: '',
        password: '',
    }

    const {
        register,
        handleSubmit,
        control,
        formState: { errors, isSubmitting },
        setError,
    } = useForm<EmployeeFormData>({
        defaultValues: employeeData,
        resolver: zodResolver(employeeSchema),
    })

    const navigate = useNavigate()

    const onSubmit = async (data: EmployeeFormData) => {
        const data_to_send: EmployeeFormProps = {
            id: employee?.id,
            name: data.name,
            email: data.email,
            role: data.role as Roles,
            username: data.email,
        }

        // Check if the email is valid
        if (!data.email.includes("@")) {
            setError("email", {message: "Invalid email address"})
            return
        }

        const method_handler = MethodHandler(method, createEmployee, updateEmployee)

        if (!method_handler) {
            setError("root", {message: "Something went wrong"})
            return
        }

        try {
            const response = await method_handler(data_to_send)
            
            // Handle successful responses
            switch (response.status) {
                case 201:
                    alert("Employee created successfully. The password is set to the default password. Upon first login, the user will be prompted to change their password.")
                    navigate("/employees")
                    break
                case 200:
                    alert("Employee updated successfully")
                    navigate("/employees")
                    break
                default:
                    setError("root", {message: "Unexpected response from server"})
                    break
            }
        } catch (error: any) {
            // Handle error responses (Axios throws for 4xx and 5xx status codes)
            const status = error.response?.status
            
            switch (status) {
                case 400:
                    setError("root", {message: "Bad Request. Please check your input."})
                    break
                case 401:
                    setError("root", {message: "Unauthorized. You do not have permission to perform this action."})
                    break
                case 403:
                    setError("root", {message: "Forbidden. You do not have permission to perform this action."})
                    break
                case 404:
                    setError("root", {message: "Employee not found."})
                    break
                case 409:
                    setError("email", {message: "Email already in use."})
                    break
                case 500:
                    setError("root", {message: "Internal server error. Please try again later."})
                    break
                default:
                    setError("root", {message: error.response?.data?.message || "An error occurred in the server"})
                    break
            }
        }
    }

    return (
        <>
            {errors.root && <Error_Component errorString={errors.root.message || "An error has occurred"} />}

            <EmployeeFormBase
                onSubmit={handleSubmit(onSubmit)}
                employee={employeeData}
                method={method}
                isProcessing={isSubmitting}
                register={register}
                control={control}
                errors={errors}
            />
        </>
    )
}